import { z } from "zod";
import { SystemEntity, SystemEvent } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

/**
 * Suggest Conversation Reply Step
 *
 * For a reply matched by detect_conversation_reply: runs the conversation
 * topic's drafting agent to produce a suggested response (persisted as a
 * threaded email draft — never sent automatically), and raises the
 * human-review task + watcher notification. For collections that means the
 * collection-email-draft agent, an AR dashboard task
 * (ar_conversation_reply_review), and an in-app notification to the thread's
 * watchers.
 *
 * No-ops (suggested=false) when the upstream detect step didn't match — wire
 * its agentConversationId output straight in.
 */
export const suggestConversationReplyStep = defineStepConfig({
  type: "suggest_conversation_reply",
  name: "Suggest Conversation Reply",
  description:
    "Drafts a suggested response to a tracked conversation's reply and raises a human-review task + notification",
  category: "AI & Agents",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The customer reply being responded to",
    ),
    agentConversationId: z
      .uuid()
      .nullable()
      .describe(
        "Conversation matched by detect_conversation_reply; null skips the step",
      ),
  }),

  outputSchema: z.object({
    suggested: z
      .boolean()
      .describe("True when a suggested reply draft was produced"),
    draftId: z
      .uuid()
      .nullable()
      .describe(
        "The suggested reply draft; null when skipped or drafting failed",
      ),
    taskId: z
      .uuid()
      .nullable()
      .describe("The human-review task; null when skipped"),
  }),

  configSchema: z.object({}),

  emits: [SystemEvent.TASK_CREATED] as const,

  execution: {
    // Runs an LLM drafting agent with context tools — allow for slow model
    // round trips before Temporal calls it stuck.
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
  },
});
